import React from 'react';
import styled from 'styled-components';
import { FaChevronRight } from 'react-icons/fa';

import { PaymentMethodIds, PaymentMethods } from './USDepositService';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import {
	createRegularPaysafeDepositRequest,
	createSecuredPaysafeDepositRequest,
	processPaysafeDepositRequest,
} from '../../../api/users';
import useNotify from '../../../hooks/useNotify';
import appConfig from '../../../appConfig';

const ACCOUNT_IDS_PLAYUP_OH = {
	VIP: 1002547810, // Not in doc, got from Linh
	Card: 1002541180,
	Skrill: 1002541200,
	Paysafecard: 1002541220,
	Paysafecash: 1002541240,
	OCT: 1002541260,
	Sightline: 1002541320,
};

const ACCOUNT_IDS_PLAYUP_IN = {
	VIP: 1002547810, // Not in doc, got from Linh
	Card: 1002530840,
	Skrill: 1002530850,
	Paysafecard: 1002530860,
	Paysafecash: 1002530870,
	OCT: 1002530980,
	Sightline: 1002602350,
};

const API_KEY = appConfig.paysafe.key;

const USDepositPaymentSelection = ({
	depositAmount,
	availablePaymentMethodIds,
	getBackToHomePage,
	onDepositSuccessful,
	onDepositError,
}: Props) => {
	const { user } = React.useContext(PortalSessionContext);
	const notify = useNotify();

	const [depositRequestId, setDepositRequestId] = React.useState<number | null>(null);
	const [paymentHandleToken, setPaymentHandleToken] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (paymentHandleToken) {
			if (!depositRequestId) {
				throw Error('Incorrect DepositRequestId: ' + depositRequestId);
			}
			processPaysafeDepositRequest(depositRequestId, paymentHandleToken);
			setDepositRequestId(null);
			setPaymentHandleToken(null);
		}
	}, [depositRequestId, paymentHandleToken]);

	const checkout = async (paymentTypeId: number) => {
		if (paymentTypeId === PaymentMethodIds.Trustly) {
			notify({
				type: 'danger',
				title: 'Error',
				message: 'Payment method is not supported.',
				timeout: 5000,
			});
			return;
		}

		let merchantRefNum: string | undefined = undefined;
		let singleUseCustomerToken: string | undefined = undefined;
		let registrationId: string | undefined = undefined;
		let consumerId: string | undefined = undefined;
		try {
			if (paymentTypeId === PaymentMethodIds.VipPreferred || paymentTypeId === PaymentMethodIds.Sightline) {
				const response = await createSecuredPaysafeDepositRequest(paymentTypeId, depositAmount);
				if (response?.meta?.redirect_registration) {
					notify({
						type: 'success',
						title: 'Registration required',
						message: 'Please complete registration on the new page and come back again.',
						timeout: 5000,
					});
					window.open(response?.meta?.redirect_registration, '_blank');
					getBackToHomePage();
					return;
				}
				if (!response?.meta?.merchant_reference_number || !response?.data?.id) {
					notify({
						type: 'danger',
						title: 'Error',
						message: 'Unable to create a deposit request. Please contact support',
						timeout: 5000,
					});
					return;
				}
				if (paymentTypeId === PaymentMethodIds.VipPreferred && !response?.meta?.registration_id) {
					notify({
						type: 'danger',
						title: 'Error',
						message: 'Unable to obtain a VipPreferred id for your deposit. Please contact support',
						timeout: 5000,
					});
					return;
				}
				if (paymentTypeId === PaymentMethodIds.Sightline && !response?.meta?.consumer_id) {
					notify({
						type: 'danger',
						title: 'Error',
						message: 'Unable to obtain a Play+ id for your deposit. Please contact support',
						timeout: 5000,
					});
					return;
				}

				merchantRefNum = response.meta.merchant_reference_number; //required
				registrationId = response.meta.registration_id; // required for VipPreferred
				consumerId = response.meta.consumer_id; // required for Sightline
				singleUseCustomerToken = response?.meta?.single_use_customer_token; //optional for Sightline
				setDepositRequestId(response?.data?.id);
			} else {
				const response = await createRegularPaysafeDepositRequest(paymentTypeId, depositAmount);
				if (!response?.meta?.merchant_reference_number || !response?.data?.id) {
					notify({
						type: 'danger',
						title: 'Error',
						message: 'Unable to create a deposit request. Please contact support',
						timeout: 5000,
					});
					return;
				}

				merchantRefNum = response.meta.merchant_reference_number; //required
				singleUseCustomerToken = response?.meta?.single_use_customer_token; //optional
				setDepositRequestId(response?.data?.id);
			}
		} catch (e: any) {
			notify({
				type: 'danger',
				title: 'Error',
				message: e?.errors?.error[0] || 'Unable to create a deposit request. Please try again later',
				timeout: 10000,
			});
			return;
		}

		console.log(paymentTypeId, merchantRefNum, registrationId);
		//6 'PUP-DR-895' 'c70aec93-e97b-459a-8998-6feb0b592ec6'

		const paysafe = window.paysafe as any;
		paysafe.checkout.setup(
			API_KEY,
			composeRequestBodyBasedOnPaymentTypeId(
				paymentTypeId,
				merchantRefNum!,
				singleUseCustomerToken,
				registrationId,
				consumerId
			),
			resultCallback,
			closeCallback
		);
	};

	const basicRequestInfo = {
		amount: depositAmount,
		currency: 'USD',
		canEditAmount: false,
		environment: 'TEST',
		locale: 'en_US',
		simulator: 'EXTERNAL',
		payoutConfig: {
			maximumAmount: 100000,
		},
	};
	const userDOB = new Date(user?.birth_date || '');
	const customerInfo = {
		customer: {
			firstName: user?.first_name,
			lastName: user?.last_name,
			email: user?.email,
			phone: user?.phone,
			dateOfBirth: {
				day: userDOB.getUTCDate(),
				month: userDOB.getUTCMonth() + 1,
				year: userDOB.getUTCFullYear(),
			},
		},
		billingAddress: {
			street: user?.street_number + ' ' + user?.street_name + ' ' + user?.street_type,
			city: user?.city,
			zip: user?.postcode,
			country: user?.country,
			state: user?.state,
		},
	};

	const composeRequestBodyBasedOnPaymentTypeId = (
		selectedPaymentTypeId: number,
		merchantRefNum: string,
		singleUseCustomerToken?: string,
		registrationId?: string,
		consumerId?: string
	) => {
		let requestObject: any = {
			merchantRefNum,
			...basicRequestInfo,
			...customerInfo,
		};
		console.log(requestObject);

		if (selectedPaymentTypeId === PaymentMethodIds.VipPreferred) {
			requestObject = {
				...requestObject,
				payout: false,
				displayPaymentMethods: ['vippreferred'],
				paymentMethodDetails: {
					vippreferred: {
						registrationId: registrationId,
						accountId: ACCOUNT_IDS_PLAYUP_IN.VIP,
					},
				},
			};
		} else if (selectedPaymentTypeId === PaymentMethodIds.Paypal) {
			requestObject = {
				...requestObject,
				payout: false,
				displayPaymentMethods: ['paypal'],
				paymentMethodDetails: {
					paypal: {
						consumerId: user?.email,
						consumerMessage: '',
						recipientDescription: 'PlayUp',
						language: 'US',
						shippingPreference: 'GET_FROM_FILE',
						orderDescription: '',
					},
				},
			};
		} else if (selectedPaymentTypeId === PaymentMethodIds.Sightline) {
			requestObject = {
				...requestObject,
				payout: false,
				singleUseCustomerToken: singleUseCustomerToken,
				displayPaymentMethods: ['sightline'],
				paymentMethodDetails: {
					sightline: {
						consumerId: consumerId,
						last4ssn: user?.ssn_last_4,
					},
				},
			};
		} else if (selectedPaymentTypeId === PaymentMethodIds.CreditCard) {
			requestObject = {
				...requestObject,
				payout: false,
				singleUseCustomerToken: singleUseCustomerToken,
				displayPaymentMethods: ['card'],
				paymentMethodDetails: {
					card: {
						accountId: ACCOUNT_IDS_PLAYUP_IN.Card,
					},
				},
			};
		} else if (selectedPaymentTypeId === PaymentMethodIds.Skrill) {
			requestObject = {
				...requestObject,
				payout: false,
				displayPaymentMethods: ['skrill'],
				paymentMethodDetails: {
					skrill: {
						consumerId: user?.email,
						emailSubject: 'Skrill Deposit', //TODO: Perhaps we need something else?
						emailMessage: 'Thank you for your deposit!', //TODO: Perhaps we need something else?
						accountId: ACCOUNT_IDS_PLAYUP_IN.Skrill,
					},
				},
			};
		} else if (selectedPaymentTypeId === PaymentMethodIds.Cash) {
			requestObject = {
				...requestObject,
				payout: false,
				displayPaymentMethods: ['paysafecash'],
				paymentMethodDetails: {
					paysafecash: {
						consumerId: user?.email,
						minAgeRestriction: 18,
						kycLevelRestriction: 'SIMPLE',
						countryRestriction: user?.country,
					},
				},
			};
		} else {
			notify({
				type: 'danger',
				title: 'Error',
				message: 'Selected payment method not available.',
				timeout: 10000,
			});
			throw Error(`Selected payment method ${selectedPaymentTypeId} not available.`);
		}
		return requestObject;
	};

	const resultCallback = (instance, error, result) => {
		if (result && result.paymentHandleToken) {
			setPaymentHandleToken(result.paymentHandleToken);
			instance.showSuccessScreen();
			onDepositSuccessful();

			// Phoenix: We could close the paysafe window upon success, so user won't see two success windows, one from us, one from them.
			// instance.close();
		} else {
			instance?.close();
			onDepositError();
			throw Error(error);
		}
	};

	// These handles are explained here: https://developer.paysafe.com/en/payments/api/#/introduction/payments-checkout/setup-function
	const closeCallback = (stage, expired) => {
		switch (stage) {
			case 'PAYMENT_HANDLE_NOT_CREATED':
				break;
			case 'PAYMENT_HANDLE_CREATED':
				break;
			case 'PAYMENT_HANDLE_REDIRECT':
				break;
			case 'PAYMENT_HANDLE_PAYABLE':
				break;
			default:
		}
	};

	if (!user) return null;

	const filteredPaymentMethods = Object.values(PaymentMethods).filter((paymentMethod) =>
		availablePaymentMethodIds.includes(paymentMethod.id.toString())
	);

	return (
		<>
			{filteredPaymentMethods.map((paymentMethod, i) => (
				<PaymentTypeRow key={paymentMethod.id} onClick={() => checkout(paymentMethod.id)}>
					<LeftContainer>
						<LogoContainer>{paymentMethod.icon}</LogoContainer>
						<MediumText>{paymentMethod.name}</MediumText>
					</LeftContainer>
					<FaChevronRight />
				</PaymentTypeRow>
			))}
		</>
	);
};

export default USDepositPaymentSelection;

interface Props {
	depositAmount: number;
	availablePaymentMethodIds: Array<string>;
	getBackToHomePage: () => void;
	onDepositSuccessful: () => void;
	onDepositError: () => void;
}

const PaymentTypeRow = styled.button`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 60px;

	&:focus {
		outline: none;
	}

	&:not(:first-of-type) {
		border-top: 1px solid black;
	}
`;

const LeftContainer = styled.div`
	display: flex;
`;

const LogoContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 110px;

	img {
		max-height: 30px;
	}
`;

const MediumText = styled.div`
	font-weight: 600;
`;
